/**
 * Determines if a booking flow step is valid based on the completion and requirement status
 * of its content modules.
 *
 * Iterates through all content modules in the step's sections and checks:
 * - If a module's status is not found in `moduleStatusMap`, it is considered valid (not required).
 * - If a module is required, it must be complete for the step to be valid.
 * - If a module is not required, its completion status does not affect validity.
 *
 * @param step - The booking flow step DTO containing sections and content modules.
 * @param moduleStatusMap - A map of module IDs to their completion and requirement status.
 * @returns `true` if all required modules are complete or not required; otherwise, `false`.
 */

export const isStepValid = (
  step: Content.Components['BookingFlowStepDto'],
  moduleStatusMap: Record<string, { isComplete: boolean; isRequired: boolean }>,
): boolean => {
  return step.sections
    .flatMap(section => section.contentModules)
    .every(module => {
      const status = moduleStatusMap[module.id];

      if (!status) return true;

      return !status.isRequired || (status.isRequired && status.isComplete);
    });
};

/**
 * Returns the first invalid (required and incomplete) module from the given booking flow step.
 *
 * Iterates through all sections and their content modules in the provided step,
 * checking the status of each module using the `moduleStatusMap`. If a module is
 * required (`isRequired`) and not complete (`!isComplete`), it is returned immediately.
 * If no such module is found, returns `undefined`.
 *
 * @param step - The booking flow step containing sections and content modules.
 * @param moduleStatusMap - A map of module IDs to their completion and requirement status.
 * @returns The first invalid booking flow module, or `undefined` if all required modules are complete.
 */

export const getFirstInvalidModule = (
  step: Content.Components['BookingFlowStepDto'],
  moduleStatusMap: Record<string, { isComplete: boolean; isRequired: boolean }>,
): Content.Components['BookingFlowModuleDto'] | undefined => {
  for (const section of step.sections) {
    for (const contentModule of section.contentModules) {
      const status = moduleStatusMap[contentModule.id];

      if (status && status.isRequired && !status.isComplete) {
        return contentModule as Content.Components['BookingFlowModuleDto'];
      }
    }
  }

  return undefined;
};
